import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Package, Building2, Truck, Clock, AlertTriangle, CheckCircle2, Factory, Boxes,
  Search, Download, RefreshCw, Loader2, X, Upload as UploadIcon, Printer,
  ArrowRight, Building, TrendingUp, PieChart as PieIcon,
} from 'lucide-react';
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import Sidebar from '../components/Sidebar';
import { getSupplyData, getSupplyRows, getSupplyVersion, supplyExcelExportUrl } from '../api_extra';

/* ─── LocalStorage helpers ─────────────────────────────────────────── */
const VERSION_KEY = 'tbc_supply_seen_at';

const STATUS_META = {
  delivered: { label: 'تم التوريد', pill: 'pill-green', color: '#22C55E' },
  pending:   { label: 'قيد التوريد', pill: 'pill-amber', color: '#F59E0B' },
  delayed:   { label: 'متأخر',      pill: 'pill-red',   color: '#EF4444' },
};

/* ─── Small primitives ───────────────────────────────────────── */

function SectionHead({ eyebrow, title, subtitle, right }) {
  return (
    <div className="flex items-end justify-between gap-4 mb-4 md:mb-5">
      <div>
        <div className="section-eyebrow">{eyebrow}</div>
        <h2 className="section-title mt-1.5">{title}</h2>
        {subtitle && <div className="text-slate-400 text-[12.5px] mt-1">{subtitle}</div>}
      </div>
      {right}
    </div>
  );
}

function KpiCard({ label, value, unit, icon: Icon, tint = '#3B82F6', hero }) {
  const cls = hero ? 'panel-hero panel-hoverable px-5 py-5' : 'panel panel-hoverable px-5 py-5';
  return (
    <div className={`${cls} flex flex-col justify-between`} style={{ minHeight: 140 }} data-testid={`supply-kpi-${label}`}>
      <div className="flex items-start justify-between gap-2 relative z-10">
        <div className="text-slate-300 text-[12.5px] font-semibold leading-tight">{label}</div>
        {Icon && (
          <div className="icon-chip" style={{ background: `${tint}22`, color: tint, border: `1px solid ${tint}44` }}>
            <Icon size={16} strokeWidth={1.75} />
          </div>
        )}
      </div>
      <div className="mt-3 relative z-10">
        <div className={`kpi-number text-[30px] leading-none ${hero ? 'text-gradient-blue' : 'text-white'}`}>{value}</div>
        {unit && <div className="text-slate-400 text-[11.5px] mt-1.5">{unit}</div>}
      </div>
    </div>
  );
}

/* ─── Supplier card — the new central visualization ─────────────── */

function SupplierCard({ supplier, onOpen }) {
  const pct = supplier.completion || 0;
  const totalQty = supplier.quantity || 0;
  return (
    <button onClick={() => onOpen(supplier)}
      data-testid={`supplier-card-${supplier.name}`}
      className="panel panel-hoverable p-5 text-right flex flex-col gap-3 min-h-[210px]">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="text-white text-[15px] font-bold truncate" title={supplier.name}>{supplier.name}</div>
          <div className="text-slate-400 text-[11.5px] mt-1">
            <span className="tabular-nums">{supplier.total.toLocaleString('en-US')}</span> توريد
          </div>
        </div>
        <div className="icon-chip icon-chip-blue"><Factory size={16} strokeWidth={1.75} /></div>
      </div>

      {/* Progress bar */}
      <div>
        <div className="flex items-center justify-between text-[11.5px] mb-1.5">
          <span className="text-slate-400">نسبة الإنجاز</span>
          <span className="text-white tabular-nums font-semibold">{pct}%</span>
        </div>
        <div className="h-2 rounded-full bg-white/[0.04] overflow-hidden">
          <div
            className="h-full rounded-full"
            style={{
              width: `${Math.min(100, pct)}%`,
              background: 'linear-gradient(90deg, #22D3EE 0%, #3B82F6 100%)',
              boxShadow: '0 0 12px rgba(59,130,246,0.45)',
            }}
          />
        </div>
      </div>

      {/* Status pills row */}
      <div className="grid grid-cols-3 gap-2 text-[11px]">
        <div className="rounded-md border border-white/5 bg-white/[0.02] px-2 py-1.5">
          <div className="flex items-center gap-1 text-emerald-300"><CheckCircle2 size={10} /> مسلم</div>
          <div className="text-white font-semibold tabular-nums mt-0.5">{supplier.delivered.toLocaleString('en-US')}</div>
        </div>
        <div className="rounded-md border border-white/5 bg-white/[0.02] px-2 py-1.5">
          <div className="flex items-center gap-1 text-amber-300"><Clock size={10} /> قيد</div>
          <div className="text-white font-semibold tabular-nums mt-0.5">{supplier.pending.toLocaleString('en-US')}</div>
        </div>
        <div className="rounded-md border border-white/5 bg-white/[0.02] px-2 py-1.5">
          <div className="flex items-center gap-1 text-red-300"><AlertTriangle size={10} /> متأخر</div>
          <div className="text-white font-semibold tabular-nums mt-0.5">{supplier.delayed.toLocaleString('en-US')}</div>
        </div>
      </div>

      <div className="mt-auto pt-2 border-t hair flex items-center justify-between text-[11.5px]">
        <span className="text-slate-400">إجمالي الكميات</span>
        <span className="text-white font-semibold tabular-nums">{totalQty.toLocaleString('en-US')}</span>
      </div>
    </button>
  );
}

/* ─── Supplier drill-down drawer ─────────────────────────────── */

function SupplierDrawer({ supplier, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!supplier) return;
    let alive = true;
    setLoading(true);
    getSupplyRows({
      filters: { company: [supplier.name] },
      sort: 'delivery_date',
      sort_dir: 'desc',
      page: 1,
      page_size: 500,
    }).then((res) => {
      if (!alive) return;
      setRows(res.rows || []);
    }).catch(() => {}).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [supplier]);

  // Escape closes
  useEffect(() => {
    if (!supplier) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [supplier, onClose]);

  const bySchool = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const key = r.school_name || r.school_id || '—';
      const b = map.get(key) || { school: key, region: r.region || '—', total: 0, delivered: 0, pending: 0, delayed: 0, qty: 0 };
      b.total += 1;
      if (r.status && b[r.status] !== undefined) b[r.status] += 1;
      b.qty += Number(r.quantity || 0);
      map.set(key, b);
    }
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [rows]);

  const filteredSchools = useMemo(() => {
    if (!q) return bySchool;
    const nq = q.toLowerCase();
    return bySchool.filter((b) => b.school.toLowerCase().includes(nq) || (b.region || '').toLowerCase().includes(nq));
  }, [q, bySchool]);

  if (!supplier) return null;

  const statusPie = [
    { name: 'تم التوريد', value: supplier.delivered, color: '#22C55E' },
    { name: 'قيد التوريد', value: supplier.pending,   color: '#F59E0B' },
    { name: 'متأخر',      value: supplier.delayed,   color: '#EF4444' },
  ].filter((s) => s.value > 0);

  return (
    <div className="fixed inset-0 z-[80]" data-testid="supplier-drawer" role="dialog" aria-modal="true">
      {/* Backdrop */}
      <div onClick={onClose} className="absolute inset-0 bg-black/60 backdrop-blur-sm fade-in" />
      {/* Drawer — slides from the RIGHT in RTL (visually the right side) */}
      <aside dir="rtl" className="absolute inset-y-0 right-0 w-full max-w-[640px] glass slide-in-right flex flex-col"
        style={{ borderLeft: '1px solid rgba(59,130,246,0.28)' }}>
        {/* Header */}
        <div className="p-5 pb-4 border-b hair panel-hero relative">
          <div className="flex items-start justify-between gap-3 relative z-10">
            <div className="min-w-0">
              <div className="section-eyebrow flex items-center gap-2"><Factory size={12} /> مورد</div>
              <h3 className="text-white text-[22px] font-extrabold tracking-tight mt-1 truncate" title={supplier.name}>{supplier.name}</h3>
              <div className="text-slate-300 text-[12.5px] mt-1">
                {supplier.total.toLocaleString('en-US')} توريد · {bySchool.length.toLocaleString('en-US')} مدرسة
              </div>
            </div>
            <button onClick={onClose} className="btn btn-ghost" data-testid="supplier-drawer-close" aria-label="إغلاق">
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Mini KPIs */}
          <div className="grid grid-cols-4 gap-3">
            <MiniKpi label="مسلم"  value={supplier.delivered} color="#22C55E" />
            <MiniKpi label="قيد"    value={supplier.pending}   color="#F59E0B" />
            <MiniKpi label="متأخر"  value={supplier.delayed}   color="#EF4444" />
            <MiniKpi label="الإنجاز" value={`${supplier.completion || 0}%`} color="#3B82F6" />
          </div>

          {/* Progress rail */}
          <div className="panel p-4">
            <div className="flex items-center justify-between text-[12px] mb-2">
              <span className="text-slate-400">نسبة إنجاز التوريد</span>
              <span className="text-white font-semibold tabular-nums">{supplier.completion || 0}%</span>
            </div>
            <div className="h-3 rounded-full bg-white/[0.04] overflow-hidden">
              <div className="h-full rounded-full"
                style={{ width: `${Math.min(100, supplier.completion || 0)}%`,
                         background: 'linear-gradient(90deg, #22D3EE 0%, #3B82F6 100%)',
                         boxShadow: '0 0 12px rgba(59,130,246,0.45)' }} />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-[11.5px]">
              <div className="flex items-center justify-between text-slate-400"><span>إجمالي الكميات</span><span className="text-white tabular-nums font-semibold">{(supplier.quantity || 0).toLocaleString('en-US')}</span></div>
              <div className="flex items-center justify-between text-slate-400"><span>عدد المدارس</span><span className="text-white tabular-nums font-semibold">{bySchool.length.toLocaleString('en-US')}</span></div>
            </div>
          </div>

          {/* Status pie */}
          {statusPie.length > 0 && (
            <div className="panel p-4">
              <div className="panel-header pb-2 mb-2"><div><div className="panel-title flex items-center gap-1.5"><PieIcon size={12} /> توزيع حالات التوريد</div></div></div>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={statusPie} dataKey="value" nameKey="name" outerRadius={78} innerRadius={44} paddingAngle={2}>
                    {statusPie.map((e, i) => <Cell key={i} fill={e.color} stroke="none" />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: '#0F172A', border: '1px solid rgba(59,130,246,0.28)', borderRadius: 8, direction: 'rtl', color: '#F5F7FA' }} />
                  <Legend iconType="circle" wrapperStyle={{ direction: 'rtl', fontSize: 12, color: '#CBD5E1' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Schools list */}
          <div className="panel p-4">
            <div className="panel-header pb-2 mb-3">
              <div><div className="panel-title flex items-center gap-1.5"><Building size={12} /> المدارس المستفيدة</div><div className="panel-subtitle">مجمعة حسب المدرسة</div></div>
              <div className="text-slate-400 text-[11.5px]">{filteredSchools.length.toLocaleString('en-US')} مدرسة</div>
            </div>
            <div className="relative mb-3">
              <Search size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث في المدرسة أو المنطقة..."
                data-testid="supplier-drawer-search"
                className="w-full bg-[#0F172A]/70 border border-white/10 rounded-lg pr-9 pl-3 py-2 text-slate-100 text-[12.5px] focus:outline-none focus:border-[#3B82F6]/50 placeholder:text-slate-500" />
            </div>
            {loading ? (
              <div className="space-y-2">
                {[1,2,3,4].map((i) => <div key={i} className="skeleton h-10 w-full" />)}
              </div>
            ) : filteredSchools.length === 0 ? (
              <div className="py-8 text-center text-slate-500 text-[12.5px]">لا توجد مدارس مطابقة</div>
            ) : (
              <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
                {filteredSchools.map((s, i) => {
                  const pct = s.total > 0 ? Math.round((s.delivered / s.total) * 100) : 0;
                  return (
                    <div key={i} className="border hair rounded-lg p-2.5 hover:bg-white/[0.03] transition-colors">
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-slate-100 text-[12.5px] font-semibold truncate" title={s.school}>{s.school}</div>
                          <div className="text-slate-500 text-[10.5px] mt-0.5">{s.region}</div>
                        </div>
                        <div className="text-left shrink-0">
                          <div className="text-white tabular-nums text-[12.5px] font-bold">{s.total.toLocaleString('en-US')}</div>
                          <div className="text-slate-500 text-[10.5px]">توريد</div>
                        </div>
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <div className="h-1.5 flex-1 rounded-full bg-white/[0.04] overflow-hidden">
                          <div className="h-full rounded-full"
                            style={{ width: `${pct}%`, background: 'linear-gradient(90deg, #22D3EE 0%, #3B82F6 100%)' }} />
                        </div>
                        <span className="text-[10.5px] text-slate-400 tabular-nums w-9 text-left">{pct}%</span>
                      </div>
                      <div className="mt-1.5 flex items-center gap-2 text-[10px]">
                        <span className="pill pill-green">مسلم: {s.delivered}</span>
                        {s.pending > 0 && <span className="pill pill-amber">قيد: {s.pending}</span>}
                        {s.delayed > 0 && <span className="pill pill-red">متأخر: {s.delayed}</span>}
                        <span className="pill pill-blue">كمية: {s.qty.toLocaleString('en-US')}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}

function MiniKpi({ label, value, color }) {
  return (
    <div className="panel p-3 text-center">
      <div className="text-slate-400 text-[10.5px] font-semibold">{label}</div>
      <div className="kpi-number text-[18px] mt-1" style={{ color }}>{typeof value === 'number' ? value.toLocaleString('en-US') : value}</div>
    </div>
  );
}

/* ─── Small charts (status + region) — kept subtle above the grid ─── */

function StatusPie({ data }) {
  if (!data?.length) return <ChartEmpty />;
  return (
    <ResponsiveContainer width="100%" height={220}>
      <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" outerRadius={80} innerRadius={46} paddingAngle={2}>
          {data.map((e, i) => <Cell key={i} fill={e.color} stroke="none" />)}
        </Pie>
        <Tooltip contentStyle={{ background: '#0F172A', border: '1px solid rgba(59,130,246,0.28)', borderRadius: 8, direction: 'rtl', color: '#F5F7FA' }} />
        <Legend formatter={(v, e) => <span style={{ color: '#CBD5E1', fontSize: 12 }}>{v} — {e.payload.value.toLocaleString('en-US')} ({e.payload.percent}%)</span>}
          iconType="circle" wrapperStyle={{ direction: 'rtl' }} />
      </PieChart>
    </ResponsiveContainer>
  );
}

function StackedRegionBar({ data }) {
  if (!data?.length) return <ChartEmpty />;
  const rows = data.slice(0, 12).map((r) => ({ name: r.name, delivered: r.delivered, pending: r.pending, delayed: r.delayed }));
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={rows} margin={{ top: 8, right: 6, left: 6, bottom: 30 }}>
        <CartesianGrid vertical={false} stroke="rgba(148,163,184,0.10)" />
        <XAxis dataKey="name" tick={{ fill: '#94A3B8', fontSize: 11 }} angle={-25} textAnchor="end" height={54} />
        <YAxis tick={{ fill: '#94A3B8', fontSize: 11 }} tickFormatter={(v) => v.toLocaleString('en-US')} />
        <Tooltip contentStyle={{ background: '#0F172A', border: '1px solid rgba(59,130,246,0.28)', borderRadius: 8, direction: 'rtl', color: '#F5F7FA' }} />
        <Legend wrapperStyle={{ fontSize: 12, direction: 'rtl' }} iconType="circle" />
        <Bar dataKey="delivered" name="تم التوريد" stackId="a" fill="#22C55E" />
        <Bar dataKey="pending"   name="قيد التوريد" stackId="a" fill="#F59E0B" />
        <Bar dataKey="delayed"   name="متأخر"      stackId="a" fill="#EF4444" radius={[6, 6, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function ChartEmpty() {
  return <div className="flex items-center justify-center h-[220px] text-slate-500 text-[13px]">لا توجد بيانات كافية للعرض</div>;
}

/* ─── Main SupplyPage ─────────────────────────────────────────────── */

export default function SupplyPage() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshNotice, setRefreshNotice] = useState(null);
  const [drawerSupplier, setDrawerSupplier] = useState(null);
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const d = await getSupplyData({});
      setData(d);
      const v = await getSupplyVersion();
      if (v?.at) localStorage.setItem(VERSION_KEY, v.at);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'تعذر تحميل بيانات التوريد');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const check = async () => {
      try {
        const v = await getSupplyVersion();
        const seen = localStorage.getItem(VERSION_KEY);
        if (v?.at && v.at !== seen) setRefreshNotice(v);
      } catch (e) {}
    };
    check();
    const onFocus = () => check();
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => { window.removeEventListener('focus', onFocus); document.removeEventListener('visibilitychange', onVisible); };
  }, []);

  const applyRefresh = async () => { setRefreshNotice(null); await load(); };
  const empty = data && !data.hasData;
  const kpis = data?.kpis || {};

  const suppliers = useMemo(() => data?.byCompany || [], [data]);
  const filteredSuppliers = useMemo(() => {
    if (!q) return suppliers;
    const nq = q.toLowerCase();
    return suppliers.filter((s) => (s.name || '').toLowerCase().includes(nq));
  }, [q, suppliers]);

  const exportExcel = () => { window.location.href = supplyExcelExportUrl(); };
  const exportPdf = () => { window.print(); };

  return (
    <div dir="rtl" className="min-h-screen flex">
      <Sidebar />
      <main className="flex-1 min-w-0" data-testid="supply-page">
        <div className="mx-auto max-w-[1440px] px-6 md:px-8 py-6 md:py-8 space-y-7 md:space-y-9">

          {/* Header */}
          <div className="flex flex-wrap items-end justify-between gap-4 no-print">
            <div>
              <div className="text-slate-400 text-[11px] font-medium flex items-center gap-2">
                <button onClick={() => nav('/')} className="hover:text-slate-200 transition-colors">لوحة القيادة</button>
                <span className="text-slate-600">›</span>
                <span className="text-slate-300">التوريدات</span>
              </div>
              <h1 className="text-white text-[26px] md:text-[30px] font-extrabold tracking-tight mt-1">
                لوحة <span className="text-gradient-blue">التوريدات</span>
              </h1>
              <div className="text-slate-400 text-[13px] mt-1">
                {data ? `${(data.filtered ?? data.total ?? 0).toLocaleString('en-US')} سجل توريد` : '—'}
                {suppliers.length > 0 && ` · ${suppliers.length} مورد`}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={load} className="btn btn-ghost" data-testid="supply-refresh">
                <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
                <span>تحديث</span>
              </button>
              <button onClick={exportExcel} className="btn btn-ghost" data-testid="supply-export-excel">
                <Download size={14} />
                <span>تصدير Excel</span>
              </button>
              <button onClick={exportPdf} className="btn btn-primary" data-testid="supply-export-pdf">
                <Printer size={14} />
                <span>تصدير PDF</span>
              </button>
            </div>
          </div>

          {/* Refresh notice */}
          {refreshNotice && (
            <div className="glass px-4 py-3 flex items-center justify-between gap-3 fade-in no-print"
                 style={{ border: '1px solid rgba(59,130,246,0.30)' }} data-testid="supply-refresh-notice">
              <div className="flex items-center gap-2 text-slate-200 text-[13px]">
                <RefreshCw size={14} className="text-[#60A5FA]" />
                <span>يتوفر ملف توريد جديد ({refreshNotice.filename || 'محدث'}) — اضغط للتحديث</span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={applyRefresh} className="btn btn-primary">تحديث الآن</button>
                <button onClick={() => { localStorage.setItem(VERSION_KEY, refreshNotice.at); setRefreshNotice(null); }} className="btn btn-ghost">لاحقاً</button>
              </div>
            </div>
          )}

          {/* Loading */}
          {loading && !data && (
            <div className="glass p-16 flex items-center justify-center gap-3 text-slate-300">
              <Loader2 size={20} className="animate-spin" />
              <span className="text-[13.5px]">جاري تحميل بيانات التوريد...</span>
            </div>
          )}
          {error && (
            <div className="panel p-4 border-red-500/30 text-red-300 text-sm">خطأ: {error}</div>
          )}

          {/* Empty state */}
          {empty && (
            <div className="glass-hero p-14 flex flex-col items-center justify-center gap-4 text-center fade-in max-w-[900px] mx-auto"
                 data-testid="supply-empty-state">
              <div className="w-20 h-20 rounded-3xl flex items-center justify-center relative z-10"
                   style={{ background: 'linear-gradient(135deg, rgba(59,130,246,0.30) 0%, rgba(14,165,233,0.14) 100%)',
                            border: '1px solid rgba(59,130,246,0.40)',
                            boxShadow: '0 12px 40px -14px rgba(59,130,246,0.55)' }}>
                <Package size={30} strokeWidth={1.6} className="text-[#60A5FA]" />
              </div>
              <div className="text-white text-[24px] font-extrabold tracking-tight relative z-10">لا توجد بيانات توريد بعد</div>
              <div className="text-slate-300 text-[13px] max-w-md leading-relaxed relative z-10">
                ارفع ملف Excel للتوريد وسنقوم بالتعرّف على الأعمدة تلقائياً (اسم المدرسة، الشركة، الصنف، الكمية، الحالة، تاريخ التوريد) ثم ستُحدَّث اللوحة فوراً.
              </div>
              <button onClick={() => nav('/imports/supply')} className="btn btn-primary relative z-10" data-testid="supply-empty-import">
                <UploadIcon size={14} />
                <span>استيراد ملف التوريد</span>
              </button>
            </div>
          )}

          {data && !empty && (
            <>
              {/* KPI cards — hero row (bold gradient) + rest (subtle) */}
              <section className="fade-in">
                <SectionHead
                  eyebrow="نظرة عامة"
                  title="مؤشرات التوريد الرئيسية"
                  subtitle="لقطة لحظية عبر جميع المدارس والشركات"
                />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
                  <KpiCard hero label="نسبة الإنجاز"     value={`${kpis.completionRate ?? 0}%`}                       unit="من إجمالي التوريد" icon={Truck}         tint="#3B82F6" />
                  <KpiCard hero label="إجمالي التوريدات"  value={(kpis.totalRows ?? 0).toLocaleString('en-US')}         unit="سجل"     icon={Package}       tint="#22D3EE" />
                  <KpiCard      label="تم التوريد"        value={(kpis.totalDelivered ?? 0).toLocaleString('en-US')}   unit="توريد"   icon={CheckCircle2}  tint="#22C55E" />
                  <KpiCard      label="قيد التوريد"       value={(kpis.pendingDeliveries ?? 0).toLocaleString('en-US')} unit="توريد"  icon={Clock}         tint="#F59E0B" />
                  <KpiCard      label="متأخر"             value={(kpis.delayedDeliveries ?? 0).toLocaleString('en-US')} unit="توريد"  icon={AlertTriangle} tint="#EF4444" />
                  <KpiCard      label="عدد الشركات"       value={(kpis.totalCompanies ?? 0).toLocaleString('en-US')}   unit="شركة"    icon={Factory}       tint="#60A5FA" />
                  <KpiCard      label="عدد المدارس"        value={(kpis.totalSchools ?? 0).toLocaleString('en-US')}    unit="مدرسة"   icon={Building2}     tint="#60A5FA" />
                  <KpiCard      label="إجمالي الكميات"    value={(kpis.totalQuantity ?? 0).toLocaleString('en-US')}    unit={`مُوَرَّد: ${(kpis.deliveredQuantity ?? 0).toLocaleString('en-US')}`} icon={Boxes} tint="#22D3EE" />
                </div>
              </section>

              {/* Small charts row (subtle) */}
              <section className="fade-in">
                <div className="grid grid-cols-12 gap-5 md:gap-6">
                  <div className="col-span-12 lg:col-span-5">
                    <div className="panel p-5">
                      <div className="panel-header"><div><div className="panel-title">نسبة حالات التوريد</div><div className="panel-subtitle">Delivered / Pending / Delayed</div></div></div>
                      <StatusPie data={data.status} />
                    </div>
                  </div>
                  <div className="col-span-12 lg:col-span-7">
                    <div className="panel p-5">
                      <div className="panel-header"><div><div className="panel-title">توريد حسب المنطقة</div><div className="panel-subtitle">مكدَّس حسب الحالة</div></div></div>
                      <StackedRegionBar data={data.byRegion} />
                    </div>
                  </div>
                </div>
              </section>

              {/* Supplier Cards — THE central visualization */}
              <section className="fade-in" data-testid="supplier-cards-section">
                <SectionHead
                  eyebrow="الموردون"
                  title="بطاقات الموردين"
                  subtitle="اضغط على أي بطاقة لعرض تفاصيل التوريدات والمدارس المستفيدة"
                  right={
                    <div className="relative w-[280px]">
                      <Search size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث عن مورد..."
                        data-testid="supplier-search"
                        className="w-full bg-[#0F172A]/70 border border-white/10 rounded-lg pr-9 pl-3 py-2 text-slate-100 text-[12.5px] focus:outline-none focus:border-[#3B82F6]/50 placeholder:text-slate-500" />
                    </div>
                  }
                />
                {filteredSuppliers.length === 0 ? (
                  <div className="panel p-10 text-center text-slate-500 text-[13px]">لا يوجد موردون مطابقون للبحث</div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                    {filteredSuppliers.map((s) => (
                      <SupplierCard key={s.name} supplier={s} onOpen={setDrawerSupplier} />
                    ))}
                  </div>
                )}
              </section>
            </>
          )}

          <div className="text-center text-slate-500 text-[11px] pt-6 no-print">
            بيانات التوريد قادمة من MongoDB — تُحدَّث تلقائياً عند رفع ملف Excel جديد
          </div>
        </div>
      </main>

      {/* Supplier drill-down drawer */}
      {drawerSupplier && <SupplierDrawer supplier={drawerSupplier} onClose={() => setDrawerSupplier(null)} />}
    </div>
  );
}
